import bbox from '@turf/bbox';
import type { Polygon } from 'geojson';

/** WGS84 mean earth radius, in metres. */
const EARTH_RADIUS_M = 6378137;
const SQ_M_PER_ACRE = 4046.8564224;
const FEET_PER_M = 3.280839895;
const DEG_TO_RAD = Math.PI / 180;
/** Metres per degree of latitude — near enough constant for a display figure. */
const M_PER_DEG_LAT = 111320;

/**
 * Spherical-excess area of a polygon's outer ring, in acres. Good to a fraction of
 * a percent at these sizes, and needs no projection.
 *
 * @param {Polygon} polygon - The polygon to measure.
 * @return {number} Area in acres.
 */
export const polygonAreaAcres = (polygon: Polygon): number => {
  const ring = polygon.coordinates[0] ?? [];
  if (ring.length < 3) {
    return 0;
  }

  let area = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[(i + 1) % ring.length];
    area +=
      (lng2 - lng1) *
      DEG_TO_RAD *
      (2 + Math.sin(lat1 * DEG_TO_RAD) + Math.sin(lat2 * DEG_TO_RAD));
  }

  const squareMetres = Math.abs((area * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
  return squareMetres / SQ_M_PER_ACRE;
};

/**
 * Width and height of a polygon's bounding box, in feet.
 *
 * Equirectangular approximation: exact enough at the scale of a drawn boundary,
 * and it keeps the figure cheap to recompute as the user edits.
 *
 * @param {Polygon} polygon - The polygon to measure.
 * @return {{ widthFt: number, heightFt: number }} The box dimensions.
 */
export const polygonBboxFeet = (
  polygon: Polygon
): { widthFt: number; heightFt: number } => {
  const [minLng, minLat, maxLng, maxLat] = bbox(polygon);
  const midLat = ((minLat + maxLat) / 2) * DEG_TO_RAD;

  const widthM = (maxLng - minLng) * M_PER_DEG_LAT * Math.cos(midLat);
  const heightM = (maxLat - minLat) * M_PER_DEG_LAT;

  return {
    widthFt: Math.abs(widthM) * FEET_PER_M,
    heightFt: Math.abs(heightM) * FEET_PER_M,
  };
};
